import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { OrderTypeConflictModal } from '@/components/common/OrderTypeConflictModal';
import { productService } from '@/services/product';
import { restaurantService, type RestaurantWithProducts } from '@/services/restaurant';
import { useRealtimeStock } from '@/hooks/useRealtimeStock';
import { useAuth } from '@/contexts/AuthContext';
import { useCartStore } from '@/store/cart';
import { checkOrderAvailability, type Restaurant } from '@/lib/operatingHours';
import { getKoreanDate } from '@/lib/dateUtils';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { DEFAULT_ECOPACK_IMAGE_URL } from '@/constants';

// Desktop-specific components
import { DesktopProductHeader } from '@/components/desktop/product/DesktopProductHeader';
import { DesktopProductImage } from '@/components/desktop/product/DesktopProductImage';
import { DesktopProductInfo } from '@/components/desktop/product/DesktopProductInfo';
import { DesktopQuantitySelector } from '@/components/desktop/product/DesktopQuantitySelector';
import { DesktopProductActions } from '@/components/desktop/product/DesktopProductActions';

interface Product {
  id: string;
  name: string;
  description?: string;
  original_price: number;
  discounted_price: number;
  discount_rate: number;
  discount_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  stock_quantity: number;
  is_available: boolean;
  image_url?: string;
}

export function DesktopProductDetailPage() {
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, ToastContainer } = useToast();
  const { user } = useAuth();
  const cartStore = useCartStore();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [restaurant, setRestaurant] = useState<RestaurantWithProducts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isOutOfStock, setIsOutOfStock] = useState(false);
  const [isOrderAvailable, setIsOrderAvailable] = useState<boolean>(false);
  const [hasActiveDeadlineOfferToday, setHasActiveDeadlineOfferToday] = useState<boolean>(true);
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [pendingOrderType, setPendingOrderType] = useState<{ currentType: string; newType: string } | null>(null);
  const [pendingDirectOrder, setPendingDirectOrder] = useState(false);

  // 쿼리 파라미터에서 주문유형 읽기
  const queryParams = new URLSearchParams(location.search);
  const orderType = (queryParams.get('orderType') as 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER') || 'DEADLINE_DISCOUNT';

  // Reuse mobile data loading logic
  useEffect(() => {
    const fetchProductDetail = async () => {
      if (!storeId || !productId) return;
      
      try {
        setIsLoading(true);
        
        // 상품 정보 조회
        const productResult = await productService.getProductById(productId);
        
        // 매장 정보 조회 (operating hours 포함)
        const storeResult = await restaurantService.getRestaurantById(storeId);
        if (storeResult.success && storeResult.data) {
          setRestaurant(storeResult.data);
        }
        
        if (productResult) {
          // 해당 주문유형의 활성화된 오퍼 선택
          const targetOffer = productResult.offers?.find(offer => 
            offer.offer_type === orderType && offer.is_active
          ) || productResult.offers?.find(offer => offer.is_active) || productResult.offers?.[0];

          // 한국 시간 기준 오늘 날짜의 재고만 선택
          const todayKST = getKoreanDate();
          const todayStock = productResult.stock?.find((s: any) => s.date === todayKST);

          // 오늘 활성 마감할인 오퍼 존재 여부 계산 (홈 화면 로직과 동일한 기준)
          if (orderType === 'DEADLINE_DISCOUNT') {
            const hasToday = (productResult.offers || []).some((offer: any) => {
              if (offer.offer_type !== 'DEADLINE_DISCOUNT' || offer.is_active !== true) return false;
              if (offer.valid_from) {
                try {
                  const offerDate = new Date(offer.valid_from).toISOString().split('T')[0];
                  return offerDate === todayKST;
                } catch {
                  return true;
                }
              }
              return true;
            });
            setHasActiveDeadlineOfferToday(hasToday);
          } else {
            setHasActiveDeadlineOfferToday(true);
          }

          // Convert Product from service to local Product interface
          const localProduct: Product = {
            id: productResult.id,
            name: productResult.name,
            description: productResult.description,
            original_price: productResult.original_price,
            discounted_price: targetOffer?.discounted_price || productResult.original_price,
            discount_rate: targetOffer?.discount_rate || 0,
            discount_type: targetOffer?.offer_type || orderType,
            stock_quantity: todayStock?.current_stock || 0,  // 오늘 재고만 사용
            is_available: productResult.is_available,
            image_url: productResult.image_url
          };
          setProduct(localProduct);
        }
      } catch (error) {
        console.error('Failed to fetch product detail:', error);
        showToast('상품 정보를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductDetail();
  }, [storeId, productId, orderType]);

  // 실시간 주문 가능 여부 계산
  useEffect(() => {
    if (restaurant && product) {
      try {
        let orderAvailability = checkOrderAvailability(
          restaurant as Restaurant, 
          product.discount_type
        );
        
        if (product.discount_type === 'DEADLINE_DISCOUNT') {
          orderAvailability = orderAvailability && hasActiveDeadlineOfferToday;
        }
        
        setIsOrderAvailable(orderAvailability);
      } catch (error) {
        console.error('Order availability check failed:', error);
        setIsOrderAvailable(false);
      }
    } else {
      setIsOrderAvailable(false);
    }
  }, [restaurant, product, hasActiveDeadlineOfferToday]);

  // 실시간 재고 업데이트 구독 (예약할인 제외)
  useRealtimeStock(
    productId && product?.discount_type !== 'RESERVATION_DISCOUNT' ? [productId] : null,
    {
      onStockUpdate: (_, newStock, isAvailable) => {
        if (typeof isAvailable === 'boolean') {
          setProduct(prev => prev ? { ...prev, stock_quantity: newStock, is_available: isAvailable } : null);
        } else {
          setProduct(prev => prev ? { ...prev, stock_quantity: newStock } : null);
        }
        setIsOutOfStock(newStock === 0 || !isAvailable);
        
        if (newStock > 0 && quantity > newStock) {
          setQuantity(newStock);
        }
      },
      onOutOfStock: (_, productName) => {
        setIsOutOfStock(true);
        setQuantity(1);
        showToast(`${productName}이(가) 품절되었습니다`);
      },
      onBackInStock: (_, productName) => {
        setIsOutOfStock(false);
        showToast(`${productName}이(가) 재입고되었습니다`);
      }
    }
  );

  // 수량 변경
  const handleQuantityChange = (newQuantity: number) => {
    if (!product) return;
    
    // 예약할인은 재고 제한 없음
    if (product.discount_type === 'RESERVATION_DISCOUNT') {
      setQuantity(newQuantity < 1 ? 1 : newQuantity);
      return;
    }
    
    if (newQuantity < 1) {
      setQuantity(1);
    } else if (newQuantity > product.stock_quantity) {
      setQuantity(product.stock_quantity);
      showToast(`재고가 ${product.stock_quantity}개만 남았습니다`);
    } else {
      setQuantity(newQuantity);
    }
  };

  // 주문 가능 상태 및 텍스트 결정
  const getAvailabilityStatus = () => {
    // 예약할인 상품은 재고 확인 없이 예약 가능
    if (product?.discount_type === 'RESERVATION_DISCOUNT') {
      return {
        text: '예약 가능',
        className: 'bg-green-100 text-green-800',
        isAvailable: true,
        reason: 'reservation'
      };
    }

    if (isOutOfStock || product?.stock_quantity === 0) {
      return {
        text: '품절',
        className: 'bg-red-100 text-red-800',
        isAvailable: false,
        reason: 'stock'
      };
    }

    if (!product?.is_available) {
      return {
        text: '준비중',
        className: 'bg-gray-100 text-gray-800',
        isAvailable: false,
        reason: 'product'
      };
    }

    if (!isOrderAvailable) {
      return {
        text: '주문마감',
        className: 'bg-gray-100 text-gray-800',
        isAvailable: false,
        reason: 'time'
      };
    }

    return {
      text: '주문가능',
      className: 'bg-blue-100 text-blue-800',
      isAvailable: true,
      reason: 'available'
    };
  };

  // 장바구니에 담기
  const handleAddToCart = async () => {
    if (!product) return;
    
    const availabilityStatus = getAvailabilityStatus();
    if (!availabilityStatus.isAvailable) {
      switch (availabilityStatus.reason) {
        case 'stock':
          showToast('품절된 상품입니다');
          break;
        case 'product':
          showToast('현재 준비 중인 상품입니다');
          break;
        case 'time':
          showToast('주문 시간이 마감되었습니다');
          break;
        default:
          showToast('주문할 수 없는 상품입니다');
      }
      return;
    }
    
    if (!user) {
      showToast('로그인이 필요합니다');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    if (product.stock_quantity < quantity) {
      showToast('선택한 수량이 재고보다 많습니다');
      return;
    }
    
    try {
      setIsAddingToCart(true);
      
      const result = await cartStore.addItem(
        storeId!,
        restaurant?.name || '',
        product.discount_type,
        {
          productId: product.id,
          productName: product.name,
          productImage: product.image_url,
          originalPrice: product.original_price,
          discountedPrice: product.discounted_price,
          discountRate: product.discount_rate,
          quantity: quantity,
          currentStock: product.stock_quantity,
          options: undefined
        },
        user?.id
      );
      
      if (result.success) {
        showToast(result.message || '장바구니에 추가되었습니다');
        
        setTimeout(() => {
          const goToCart = window.confirm('장바구니로 이동하시겠습니까?');
          if (goToCart) {
            navigate('/cart');
          }
        }, 1000);
      } else {
        if (result.message === 'DIFFERENT_ORDER_TYPE' && result.data) {
          setPendingOrderType({
            currentType: result.data.currentOrderType,
            newType: result.data.newOrderType
          });
          setShowOrderTypeModal(true);
        } else {
          showToast(result.message || '장바구니 추가에 실패했습니다');
        }
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showToast('장바구니에 추가하는데 실패했습니다');
    } finally {
      setIsAddingToCart(false);
    }
  };

  // 바로 주문하기
  const handleOrderNow = async () => {
    if (!product) return;
    
    const availabilityStatus = getAvailabilityStatus();
    if (!availabilityStatus.isAvailable) {
      switch (availabilityStatus.reason) {
        case 'stock':
          showToast('품절된 상품입니다');
          break;
        case 'product':
          showToast('현재 준비 중인 상품입니다');
          break;
        case 'time':
          showToast('주문 시간이 마감되었습니다');
          break;
        default:
          showToast('주문할 수 없는 상품입니다');
      }
      return;
    }
    
    if (!user) {
      showToast('로그인이 필요합니다');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    if (product.stock_quantity < quantity) {
      showToast('선택한 수량이 재고보다 많습니다');
      return;
    }
    
    const result = await cartStore.addItem(
      storeId!,
      restaurant?.name || '',
      product.discount_type,
      {
        productId: product.id,
        productName: product.name,
        productImage: product.image_url,
        originalPrice: product.original_price,
        discountedPrice: product.discounted_price,
        discountRate: product.discount_rate,
        quantity: quantity,
        currentStock: product.stock_quantity,
        options: undefined
      },
      user?.id
    );
    
    if (result.success) {
      showToast('장바구니에 추가되어 주문 페이지로 이동합니다');
      setTimeout(() => {
        navigate('/cart');
      }, 1000);
    } else {
      if (result.message === 'DIFFERENT_ORDER_TYPE' && result.data) {
        setPendingOrderType({
          currentType: result.data.currentOrderType,
          newType: result.data.newOrderType
        });
        setPendingDirectOrder(true);
        setShowOrderTypeModal(true);
      } else if (result.message?.includes('이전 장바구니가 초기화')) {
        showToast(result.message);
        navigate('/cart');
      } else {
        showToast(result.message || '장바구니 추가에 실패했습니다');
      }
    }
  };

  // 주문 유형 모달 핸들러
  const handleOrderTypeConfirm = async () => {
    if (!product || !pendingOrderType) return;
    
    await cartStore.clearCart(user?.id);
    const result = await cartStore.addItem(
      storeId!,
      restaurant?.name || '',
      product.discount_type,
      {
        productId: product.id,
        productName: product.name,
        productImage: product.image_url,
        originalPrice: product.original_price,
        discountedPrice: product.discounted_price,
        discountRate: product.discount_rate,
        quantity: quantity,
        currentStock: product.stock_quantity,
        options: undefined
      },
      user?.id
    );
    
    setShowOrderTypeModal(false);
    setPendingOrderType(null);
    
    if (result.success) {
      if (pendingDirectOrder) {
        showToast('기존 상품을 삭제하고 새 상품을 담아 주문 페이지로 이동합니다');
        setTimeout(() => {
          navigate('/cart');
          setPendingDirectOrder(false);
        }, 1000);
      } else {
        showToast('기존 상품을 삭제하고 새 상품을 담았습니다');
        setTimeout(() => {
          const goToCart = window.confirm('장바구니로 이동하시겠습니까?');
          if (goToCart) {
            navigate('/cart');
          }
        }, 1000);
      }
    } else {
      showToast('상품을 장바구니에 담는데 실패했습니다');
      setPendingDirectOrder(false);
    }
  };

  const handleOrderTypeCancel = () => {
    setShowOrderTypeModal(false);
    setPendingOrderType(null);
    setPendingDirectOrder(false);
  };

  // 주문 유형 텍스트
  const getOrderTypeText = (type: string) => {
    switch (type) {
      case 'DEADLINE_DISCOUNT':
        return '마감할인';
      case 'RESERVATION_DISCOUNT':
        return '예약할인';
      case 'REGULAR_ORDER':
        return '일반주문';
      default:
        return type;
    }
  };

  const availabilityStatus = getAvailabilityStatus();

  if (isLoading) {
    return (
      <DesktopLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">상품 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (!product) {
    return (
      <DesktopLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-[18px] text-gray-600 mb-6">상품 정보를 찾을 수 없습니다</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 text-[16px] font-medium"
            >
              돌아가기
            </button>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  const totalPrice = product.discounted_price * quantity;
  const productImageUrl = product.image_url || (product.discount_type === 'DEADLINE_DISCOUNT' ? DEFAULT_ECOPACK_IMAGE_URL : undefined);

  return (
    <DesktopLayout>
      <div className="bg-white">
        {/* Header */}
        <DesktopProductHeader onBack={() => navigate(-1)} />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 py-8">
          {/* Left: Product Image */}
          <DesktopProductImage 
            imageUrl={productImageUrl} 
            productName={product.name} 
          />
          
          {/* Right: Product Information */}
          <div className="space-y-8">
            {/* Product Info */}
            <DesktopProductInfo
              product={product}
              orderType={getOrderTypeText(product.discount_type)}
              availabilityStatus={availabilityStatus}
            />

            {/* Quantity Selector */}
            <DesktopQuantitySelector
              quantity={quantity}
              maxQuantity={product.discount_type === 'RESERVATION_DISCOUNT' ? null : product.stock_quantity}
              onQuantityChange={handleQuantityChange}
              isDisabled={!availabilityStatus.isAvailable}
              totalPrice={totalPrice}
              orderType={product.discount_type}
            />

            {/* Action Buttons */}
            <DesktopProductActions
              availabilityStatus={availabilityStatus}
              isAddingToCart={isAddingToCart}
              onAddToCart={handleAddToCart}
              onOrderNow={handleOrderNow}
            />
          </div>
        </div>

        {/* 주문유형 충돌 모달 */}
        {pendingOrderType && (
          <OrderTypeConflictModal
            isOpen={showOrderTypeModal}
            currentOrderType={pendingOrderType.currentType}
            newOrderType={pendingOrderType.newType}
            onConfirm={handleOrderTypeConfirm}
            onCancel={handleOrderTypeCancel}
          />
        )}
        
        <ToastContainer />
      </div>
    </DesktopLayout>
  );
}
